import React, { useEffect, useRef, useState } from "react";
import { api, mediaUrl, fmtDate } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ClipboardCheck, Wrench, Upload, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function MaintenanceWriteupCard() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [active, setActive] = useState(null);

  const load = async () => {
    try {
      const [w, a] = await Promise.all([
        api.get("/maintenance/awaiting-writeup"),
        api.get("/assets"),
      ]);
      setItems(w.data || []);
      setAssets(a.data || []);
    } catch { /* non-critical dashboard card — stay silent */ }
  };
  useEffect(() => { load(); }, []);

  if (items.length === 0) return null;
  const assetName = (id) => assets.find((a) => a.id === id)?.name || "—";

  return (
    <div className="ledger-card p-5" data-testid="maintenance-writeup-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#FBBF24] flex items-center gap-1.5">
            <ClipboardCheck className="w-3.5 h-3.5" /> {t("dashboard.writeup.label")}
          </div>
          <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{t("dashboard.writeup.title")}</h3>
        </div>
        <span className="badge-pill badge-pending" data-testid="writeup-count">{items.length}</span>
      </div>
      <p className="text-xs text-[#94A3B8] mb-3">{t("dashboard.writeup.intro")}</p>
      <div className="space-y-2">
        {items.map((tk) => {
          const daysAgo = Math.floor((new Date() - new Date(tk.effective_end)) / 86400000);
          return (
            <button
              key={tk.id}
              onClick={() => setActive(tk)}
              data-testid={`writeup-row-${tk.id}`}
              className="w-full text-left p-3 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#FBBF24]/50 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm break-words flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5 text-[#FBBF24] flex-shrink-0" /> {tk.title}
                </div>
                <div className="font-mono text-[11px] text-[#94A3B8] mt-0.5">
                  {assetName(tk.asset_id)}
                  {tk.scheduled_for && <> · {fmtDate(tk.scheduled_for)} → {fmtDate(tk.effective_end)}</>}
                  {daysAgo >= 0 && <> · {t("dashboard.writeup.endedAgo", { n: daysAgo })}</>}
                </div>
              </div>
              <span className="self-start sm:self-auto px-3 py-1.5 rounded-md bg-[#FBBF24]/10 border border-[#FBBF24]/30 text-[#FBBF24] text-xs whitespace-nowrap flex items-center gap-1 flex-shrink-0">
                <ClipboardCheck className="w-3 h-3" /> {t("dashboard.writeup.recordBtn")}
              </span>
            </button>
          );
        })}
      </div>

      {active && (
        <WriteupDialog
          ticket={active}
          assetName={assetName(active.asset_id)}
          onClose={() => setActive(null)}
          onSaved={() => { setActive(null); load(); }}
        />
      )}
    </div>
  );
}

function WriteupDialog({ ticket, assetName, onClose, onSaved }) {
  const t = useT();
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const pickFiles = (list) => {
    const arr = Array.from(list || []).filter(
      (f) => f.type?.startsWith("image/") || /\.(heic|heif|jpg|jpeg|png|webp)$/i.test(f.name),
    );
    if (arr.length) setFiles((p) => [...p, ...arr]);
  };
  const removeFile = (idx) => setFiles((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!notes.trim()) { toast.error(t("dashboard.writeup.notesRequired")); return; }
    setSaving(true);
    try {
      const photoIds = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("linked_type", "maintenance");
        fd.append("linked_id", ticket.id);
        try {
          const { data } = await api.post("/media/upload", fd);
          photoIds.push(data.id);
        } catch {
          toast.error(`Photo "${f.name}" failed to upload`);
        }
      }
      await api.post(`/maintenance/${ticket.id}/complete`, {
        completion_notes: notes.trim(),
        completion_photo_ids: photoIds,
        mark_resolved: true,
      });
      toast.success(t("dashboard.writeup.savedToast"));
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="writeup-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-[#FBBF24]" /> {t("dashboard.writeup.dialogTitle")}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#94A3B8]">{t("dashboard.writeup.intro")}</DialogDescription>
        </DialogHeader>

        <div className="ledger-card !bg-[#050A14] p-3 text-xs" data-testid="writeup-ticket-summary">
          <div className="font-medium text-sm">{ticket.title}</div>
          <div className="font-mono text-[11px] text-[#94A3B8] mt-0.5">
            {assetName}
            {ticket.scheduled_for && <> · {fmtDate(ticket.scheduled_for)} → {fmtDate(ticket.effective_end)}</>}
          </div>
        </div>

        <div className="mt-3">
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("dashboard.writeup.notesLabel")}</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("dashboard.writeup.notesPlaceholder")}
            rows={6}
            data-testid="writeup-notes-input"
            className="bg-[#050A14] border-[#1E293B] mt-1"
          />
        </div>

        <div className="mt-3">
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("dashboard.writeup.photosLabel")}</Label>
          <div
            onClick={() => fileRef.current?.click()}
            className="mt-1 border border-dashed border-[#1E293B] rounded-md p-5 text-center cursor-pointer hover:border-[#FBBF24] transition-colors"
            data-testid="writeup-photo-dropzone"
          >
            <Upload className="w-5 h-5 mx-auto mb-1.5 text-[#94A3B8]" />
            <div className="text-xs">{t("dashboard.writeup.photosDrop")}</div>
            <input
              ref={fileRef} type="file" multiple accept="image/*,.heic,.heif"
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              onChange={(e) => { pickFiles(e.target.files); e.target.value = ""; }}
              data-testid="writeup-photo-input"
            />
          </div>
          {files.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mt-2" data-testid="writeup-photo-previews">
              {files.map((f, idx) => (
                <div key={idx} className="relative aspect-video bg-[#050A14] border border-[#1E293B] rounded-md overflow-hidden">
                  <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button" onClick={() => removeFile(idx)}
                    data-testid={`writeup-photo-remove-${idx}`}
                    className="absolute top-1 right-1 bg-[#0B1221]/90 rounded p-0.5 text-[#F87171] hover:text-[#EF4444]"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving} data-testid="writeup-save-btn" className="bg-[#10B981] hover:bg-[#059669]">
            <CheckCircle2 className="w-4 h-4 mr-1" /> {saving ? t("common.saving") : t("dashboard.writeup.saveBtn")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
